import {CastorService} from '@aipb/gulliver/castor/data-access';
import {CastorError, CastorErrorCode, GetRecord, GetRecordReportParams, Report} from '@aipb/gulliver/castor/model';
import {CatalogEnvService} from '@aipb/gulliver/catalog/env';
import {CustomRecordItem, DetailItem, DetailTab} from '@aipb/gulliver/catalog/model';
import {CatalogRouting} from '@aipb/gulliver/catalog/routing';
import {CatalogXmlParserService, createDetailCompositeKey, HIGHLIGHT_REGEX} from '@aipb/gulliver/catalog/util';
import {SnackbarService} from '@aipb/shared/util/snackbar';
import {Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {captureException, withScope} from '@sentry/angular';
import isArray from 'lodash-es/isArray';
import isString from 'lodash-es/isString';
import {Observable, of, throwError} from 'rxjs';
import {catchError, finalize, map, tap} from 'rxjs/operators';
import {DetailRepository} from './detail.repository';

@Injectable({
  providedIn: 'root'
})
export class DetailService {
  constructor(
    private repository: DetailRepository,
    private castor: CastorService,
    private xmlParser: CatalogXmlParserService,
    private catalogEnv: CatalogEnvService,
    private transloco: TranslocoService,
    private snackbar: SnackbarService,
    private catalogRouting: CatalogRouting
  ) {
    this.handleError = this.handleError.bind(this);
  }

  get configId() {
    return this.catalogEnv.activeConfigId;
  }

  addTab(tab: DetailTab, active = false): void {
    if (this.repository.hasTab(tab.id)) {
      if (active) {
        this.setActiveTab(tab.id);
      }
      return;
    }

    this.repository.addTab(tab);

    if (active) {
      this.setActiveTab(tab.id);
    }
  }

  fetchDetail(recordId: string, coreId: string, query: string): Observable<DetailItem> {
    const detail = this.repository.getDetail(createDetailCompositeKey(coreId, recordId));

    if (detail) {
      return of(detail);
    }

    this.repository.setLoading(true);

    return this.castor
      .getRecord<CustomRecordItem>({
        recordId: recordId,
        coreId: coreId
        /*highlighting: {
          ...DEFAULT_HIGHLIGHTING_PARAMS,
          q: query,
          ...(this.env.catalog?.fulltext?.highlighting || {})
        }*/
      })
      .pipe(
        catchError((error) => {
          this.handleError(error, coreId, recordId);

          return throwError(error);
        }),
        map((data) => {
          try {
            if (this.catalogEnv.env.headerFormat === 'html') {
              data.document.header = <any>{
                label: {
                  value: data.document.GulliverTabName
                }
              }
            }

            if (this.catalogEnv.env.headerFormat === 'xml') {
              data.document.header = this.xmlParser.parseCastorHeader(
                data.document[this.catalogEnv.env.castor.UUID],
                data.document.GulliverRecHeader
              );
            }

            if (this.catalogEnv.env.headerFormat === 'json') {
              data.document.header = JSON.parse(data.document.GulliverRecHeader);
            }

            data.document.header.id = data.document[this.catalogEnv.env.castor.UUID];

            data.document.showLabel = true;
            data.document.showSublabel = true;
            data.document.showAdditional = true;

            data.document.details = [];

            const gulliverRecDetail = data.document.GulliverRecDetail;
            if (gulliverRecDetail) {
              if (isArray(gulliverRecDetail)) {
                for (const d of gulliverRecDetail) {
                  data.document.details.push(JSON.parse(d));
                }
              }

              if (isString(gulliverRecDetail)) {
                data.document.details.push(JSON.parse(gulliverRecDetail));
              }
            }

            data.document.fetch = this.xmlParser.parseCastorFetch3if(data.document.GulliverFetch3if);
          } catch (e) {
            withScope((scope) => {
              scope.setExtra('CASTOR_ID', data.document[this.catalogEnv.env.castor.UUID]);
              scope.setExtra('CASTOR_DATA', data.document);
              captureException(e);
            });
          }

          const d = this.createDetailItem(data, coreId, query);
          this.repository.addDetail(d);

          return d;
        }),
        finalize(() => {
          this.repository.setLoading(false);
        })
      );
  }

  fetchReport(recordId: string, report: Report) {
    const detailId = createDetailCompositeKey(report.coreId, recordId);

    const detail = this.repository.getDetail(detailId);
    const lang = this.transloco.getActiveLang();
    const reportHighlightMode = this.catalogEnv.env.reportHighlightMode;

    const params: GetRecordReportParams = {
      recordId: recordId,
      reportId: report.reportId,
      reportOutput: 'json',
      reportLang: lang,
      coreId: detail.coreId
    };

    /*if (reportHighlightMode === 'all_fields') {
      params['highlighting'] = {
        ...DEFAULT_HIGHLIGHTING_PARAMS,
        q: detail.query,
        requireFieldMatch: false
      };
    }

    if (reportHighlightMode === 'search_fields') {
      params['highlighting'] = {
        ...DEFAULT_HIGHLIGHTING_PARAMS,
        q: detail.query,
        requireFieldMatch: true
      };
    }*/

    return this.castor.getRecordReport(params).pipe(
      catchError((error) => {
        this.handleError(error, detail.coreId, recordId);

        return throwError(error);
      }),
      map((data) => {
        return JSON.parse(data.replace(HIGHLIGHT_REGEX, '<em>$2</em>'));
      }),
      tap((data) => {
        this.repository.updateReport(detail.id, report, lang, data);
      })
    );
  }

  removeAll(): void {
    this.repository.removeDetails();
    this.repository.removeTabs();
  }

  setActiveTab(id: string) {
    const tabs = this.repository.getTabs();

    this.repository.setActiveTabIndex(tabs.findIndex((t) => t.id === id));
  }

  private createDetailItem(record: GetRecord<CustomRecordItem>, coreId: string, query: string): DetailItem {
    return {
      ...record,
      id: createDetailCompositeKey(coreId, record.document[this.catalogEnv.env.castor.UUID]),
      recordId: record.document[this.catalogEnv.env.castor.UUID],
      reports: {},
      imageThumb: record.document.header?.images?.[0]?.thumb,
      imageLarge: record.document.header?.images?.[0]?.large,
      coreId: coreId,
      query: query
    };
  }

  private handleError(error, coreId, recordId) {
    if (error instanceof CastorError && error.code) {
      const id = createDetailCompositeKey(coreId, recordId);
      const detail = this.repository.getDetail(id);

      if (detail) {
        this.repository.removeDetail(detail.id);
      }

      if (error.code === CastorErrorCode.RECORD_ID_NOT_FOUND) {
        this.snackbar.open(this.transloco.translate('catalog.messages.castor_record_not_found'), 'warn', {
          duration: 5000
        });
      }

      if (error.code === CastorErrorCode.INSUFFICIENT_AUTHORIZATION) {
        this.snackbar.open(this.transloco.translate('catalog.messages.castor_not_authorized'), 'warn', {
          duration: 5000
        });
        this.catalogRouting.detailDefault();
      }
    }
  }
}
